import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  getRedemptions,
  markRedemptionPaid,
  markRedemptionFailed,
  hasPendingRedemptions,
} from '../api';
import { notifyRedemptionsPending } from '../api';
import {
  Paper, Box, Button, Chip, Typography, Snackbar, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, IconButton
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PageHeader from './common/PageHeader';
import { useLiveActivity } from '../hooks/useLiveActivity';

// Format pennies -> USD (always 2 decimals) for reward_amount
const formatRewardAmount = (v) => {
  if (v === null || v === undefined) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return (n / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Generate PayPal transaction note
const generatePayPalNote = (row) => {
  const tokenAmount = Number(row?.token_amount);
  const rewardAmount = Number(row?.reward_amount); // in pennies
  
  if (!tokenAmount || !rewardAmount || tokenAmount === 0) {
    return 'N/A';
  }
  
  // Calculate multiplier: reward_amount (pennies) / token_amount
  const multiplier = rewardAmount / tokenAmount;
  
  // Format multiplier to 1-2 decimal places, removing unnecessary trailing zeros
  const multiplierStr = multiplier % 1 === 0 
    ? multiplier.toFixed(0) 
    : multiplier.toFixed(2).replace(/\.?0+$/, '');
  
  return `Bidsy cash redemption: ${tokenAmount} tokens with a ${multiplierStr}X cash-out bonus.`;
};

const STATUS_OPTIONS = ['pending', 'paid', 'failed', 'all'];

export default function ProcessRedemptions() {
  const [endDate, setEndDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(13, 'day'));
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  // Pending is the work queue: every unpaid request shows, however old. The date
  // range only applies to the history tabs. It used to apply here too, so a
  // request left unpaid for more than two weeks dropped off this page.
  const datesApply = status !== 'pending';
  const params = useMemo(() => ({
    status,
    ...(datesApply ? {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    } : {}),
    limit: 500,
    offset: 0
  }), [status, datesApply, startDate, endDate]);
  const { refresh: refreshLiveActivity } = useLiveActivity();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getRedemptions(params);
      setRows(res?.data || []);
      setMeta(res?.meta || null);
      // Let the nav know (always check the global pending state, not just current filter)
      try {
        const hasAny = await hasPendingRedemptions();
        notifyRedemptionsPending(hasAny);
      } catch {}
      // Keep the dashboard's "Cash-outs to pay" count in step with this page.
      refreshLiveActivity();
    } catch (e) {
      setToast({ severity: 'error', msg: 'Failed to load redemptions' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchData(); }, [status, startDate, endDate]);

  const columns = useMemo(() => {
    if (!rows?.length) return [];
    const cols = Object.keys(rows[0]);
    return cols;
  }, [rows]);

  const doMarkPaid = async (id) => {
    setBusyId(id);
    try {
      const r = await markRedemptionPaid(id);
      setToast({ severity: r?.success ? 'success' : 'warning', msg: r?.message || 'Processed' });
      await fetchData();
      try {
        const hasAny = await hasPendingRedemptions();
        notifyRedemptionsPending(hasAny);
      } catch {}
    } catch {
      setToast({ severity: 'error', msg: 'Failed to mark as paid' });
    } finally {
      setBusyId(null);
    }
  };

  const doMarkFailed = async (id) => {
    const defaultReason = 'PayPal account not found or inactive';
    const reason = window.prompt('Enter failure reason:', defaultReason) || defaultReason;
    setBusyId(id);
    try {
      const r = await markRedemptionFailed(id, reason);
      setToast({ severity: r?.success ? 'success' : 'warning', msg: r?.message || 'Marked failed' });
      await fetchData();
      try {
        const hasAny = await hasPendingRedemptions();
        notifyRedemptionsPending(hasAny);
      } catch {}
    } catch {
      setToast({ severity: 'error', msg: 'Failed to mark as failed' });
    } finally {
      setBusyId(null);
    }
  };

  const getRedemptionId = (row) => row?.redemption_id ?? row?.id ?? row?.transaction_id;

  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setToast({ severity: 'success', msg: 'Email copied to clipboard!' });
    } catch (err) {
      console.error('Failed to copy email:', err);
      setToast({ severity: 'error', msg: 'Failed to copy email' });
    }
  };

  // Identify email column (check common field names)
  const emailColumn = useMemo(() => {
    if (!rows?.length) return null;
    const firstRow = rows[0];
    const emailFields = ['paypal_email', 'email', 'user_email', 'paypal_account'];
    return emailFields.find(field => field in firstRow) || null;
  }, [rows]);

  // Enhanced columns with PayPal Note
  const enhancedColumns = useMemo(() => {
    if (!columns.length) return [];
    // Add "PayPal Note" column after the existing columns but before Actions
    return [...columns, 'paypal_note'];
  }, [columns]);

  return (
    <>
      <PageHeader title="Process redemptions" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Status:</Typography>
          {STATUS_OPTIONS.map((s) => (
            <Button
              key={s}
              variant={status === s ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setStatus(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <DatePicker
            label="Start"
            value={startDate}
            onChange={(d) => d && setStartDate(d)}
            disabled={!datesApply}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End"
            value={endDate}
            onChange={(d) => d && setEndDate(d)}
            disabled={!datesApply}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button variant="outlined" onClick={fetchData}>{datesApply ? 'Apply' : 'Refresh'}</Button>
          {meta && (
            <Chip
              variant="outlined"
              label={meta.start
                ? `Window: ${meta.start.slice(0,10)} → ${meta.end?.slice(0,10)} • ${meta.total ?? rows.length} found`
                : `Every unpaid request, any date • ${meta.total ?? rows.length} found`}
            />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            No redemptions match your filters.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {enhancedColumns.map((c) => (
                  <TableCell key={c} sx={{ fontWeight: 'bold' }}>
                    {c === 'paypal_note' ? 'PayPal Note' : c}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => {
                const rowStatus = String(row?.status || '').toLowerCase();
                const isPending = rowStatus === 'pending';
                const rid = getRedemptionId(row);
                return (
                  <TableRow key={i} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                    {enhancedColumns.map((c) => {
                      // Special handling for PayPal Note column
                      if (c === 'paypal_note') {
                        const note = generatePayPalNote(row);
                        return (
                          <TableCell key={c}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {note}
                              {note !== 'N/A' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyEmail(note)}
                                  sx={{ padding: '4px' }}
                                  title="Copy note to clipboard"
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        );
                      }
                      
                      const raw = row[c];
                      const display = (c === 'reward_amount')
                        ? formatRewardAmount(raw)
                        : (raw === null || raw === undefined ? '' : String(raw));
                      
                      // Special handling for email column with copy icon
                      if (emailColumn && c === emailColumn && raw) {
                        return (
                          <TableCell key={c}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {display}
                              <IconButton
                                size="small"
                                onClick={() => handleCopyEmail(String(raw))}
                                sx={{ padding: '4px' }}
                                title="Copy email to clipboard"
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        );
                      }
                      
                      // Special handling for reward_amount column with copy icon
                      if (c === 'reward_amount' && raw !== null && raw !== undefined) {
                        const numericValue = Number(raw);
                        if (Number.isFinite(numericValue)) {
                          const valueWithoutDollar = (numericValue / 100).toFixed(2);
                          return (
                            <TableCell key={c}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {display}
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyEmail(valueWithoutDollar)}
                                  sx={{ padding: '4px' }}
                                  title="Copy amount to clipboard"
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          );
                        }
                      }
                      
                      return <TableCell key={c}>{display}</TableCell>;
                    })}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {isPending ? (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            sx={{ mr: 1 }}
                            disabled={busyId === rid}
                            onClick={() => doMarkPaid(rid)}
                          >
                            {busyId === rid ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Processed'}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={busyId === rid}
                            onClick={() => doMarkFailed(rid)}
                          >
                            {busyId === rid ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Failed'}
                          </Button>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          label={rowStatus || '—'}
                          color={rowStatus === 'paid' ? 'success' : (rowStatus === 'failed' ? 'error' : 'default')}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : null}
      </Snackbar>
    </>
  );
}


